import React, { useEffect, useState } from 'react';
import { createSelector } from 'reselect';
import styled, { StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import { joyDocHelper } from '@builttocreate/engine-utils';
import { applyLogic } from '@joyfill/conditional-logic';
import { PDFRenderer } from '@joyfill/layouts';
import Field from '../fields/Field';
import ExportFormPage from './ExportFormPage';
import { formulas } from '../formulas';
import {
  calculatePagePercentageAdjustments,
  reversePercentageAdjustedValue,
  getFormattedFieldPositionForMerging,
  getTableFormattedPageFields,
  isFieldHiddenInView,
} from '../../../lib/documentHelper';

import { validateLicense } from '../../../lib/licenseValidator.js';

import { ThemeProvider } from '../../../theme/ThemeContext';
import getThemeObject, { getFieldThemeObject } from '../../../theme/getThemeObject';

import { Red } from '../../../constants/Colors';
import FieldModes from '../../../constants/FieldModes';
import FieldTypes from '../../../constants/FieldTypes';
import FileViews from '../../../constants/FileViews';
import PDFRendererFieldLayoutTypes from '../../../constants/PDFRenderFieldLayoutTypes';
import FieldPresentationModes from '../../../constants/FieldPresentationModes';
import PageLayoutModes from '../../../constants/PageLayoutModes';

const { getPageOrder, sortPages, getCleanedJoyDoc } = joyDocHelper;

/**
 * Selector that cleans the document and resolves any formulas
 * Returns a cleaned document with all formula values calculated
 */
/**
 * @private
 */
const getCleanedAndResolvedDoc = createSelector(
  (doc) => doc,
  (doc) => {
    // First clean the document
    let cleanedDoc = getCleanedJoyDoc(doc);

    // Then resolve formulas if they exist
    if (cleanedDoc.formulas && Array.isArray(cleanedDoc.formulas) && cleanedDoc.formulas.length > 0) {
      // Check if any fields have formula applications
      const hasFieldsWithFormulas = cleanedDoc.fields.some(field =>
        field.formulas && Array.isArray(field.formulas) && field.formulas.length > 0
      );

      if (hasFieldsWithFormulas) {
        try {
          // Resolve all formulas in the document
          const formulaResult = formulas.resolve(cleanedDoc);

          if (formulaResult.status === 'valid' || formulaResult.status === 'warning') {
            // Use the resolved doc that contains updated field values
            cleanedDoc = formulaResult.doc;

            // Log warnings for debugging (non-blocking)
            if (formulaResult.warnings && formulaResult.warnings.length > 0) {
              console.warn('JoyDocExporter: Formula resolution warnings:', formulaResult.warnings);
            }
          }
        } catch (error) {
          // Log error but don't block the export
          console.error('JoyDocExporter: Error resolving formulas:', error);
        }
      }
    }

    return cleanedDoc;
  }
);

const StyledPageWrapper = styled.div`
  font-family:  ${(props) => props.theme.fontFamily};

  * {
    scrollbar-color: transparent transparent;

    &::-webkit-scrollbar {
      width: 0px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: transparent;
    }
  }
`;

/**
 * Custom shouldForwardProp function for styled-components v6 with React 19.
 * Filters out custom styled-component props from being passed to the DOM.
 * Uses @emotion/is-prop-valid as the base validation.
 */
const shouldForwardProp = (propName, target) => {
  // For custom components (functions/classes), forward all props
  if (typeof target === 'function') {
    return true;
  }
  // For string targets (DOM elements like 'div', 'span', 'svg'), use isPropValid
  if (typeof target === 'string') {
    return isPropValid(propName);
  }
  // Default: forward all props for unknown target types
  return true;
};

const JoyDocExporter = ({ doc, theme, license, config = { page: { height: 1056, width: 816, padding: 0 } } }) => {

  /**
   * Don't attempty to render if doc is undefined.
   */
  if (!doc) return null;

  /**
   * Handle license validation
   */
  const [licenseValidation, setLicenseValidation] = useState(null);
  useEffect(() => {
    const handleLicenseValidation = async (license) => {
      const validation = await validateLicense(license);
      if (!validation.isValid) console.warn('LICENSE_VALIDATION_ERROR: ', validation.error);
      setLicenseValidation(validation);
    }
    handleLicenseValidation(license);
  }, []);



  /**
   * Ensure doc is a JoyDoc and notify if not.
   */
  if (doc && (!doc.files || !doc.fields)) {

    console.warn('JoyDocExporter: doc is not a valid JoyDoc JSON');

    return (
      <>
        <div style={{ textAlign: 'center', maxWidth: config?.page?.width ? `${config?.page?.width}px` : '400px' }}>
          <h1 style={{ color: Red }}>Oops. Unable to render PDF</h1>
          <p style={{ color: Red }}>Invalid JoyDoc JSON has been detected.</p>
        </div>
      </>
    );
  }

  /**
   * Ensure proper config is passed in.
   */
  if (config) {
    /* eslint-disable-next-line no-console */
    if (!config.page) console.warn('JoyDocExporter: config.page is required');
    /* eslint-disable-next-line no-console */
    if (typeof config.page?.height !== 'number') console.warn('JoyDocExporter: config.page.height must be a number');
    /* eslint-disable-next-line no-console */
    if (typeof config.page?.width !== 'number') console.warn('JoyDocExporter: config.page.width must be a number');
    /* eslint-disable-next-line no-console */
    if (typeof config.page?.padding !== 'number') console.warn('JoyDocExporter: config.page.padding must be a number');
  }

  // Get cleaned and formula-resolved document
  const cleanedDoc = getCleanedAndResolvedDoc(doc);

  const [measurements, setMeasurments] = React.useState({});
  useEffect(() => { setMeasurments({}); }, [doc]);

  const { pdfPages, pdfHeader, pdfFooter } = formatPagesForPDFRenderer({ doc: cleanedDoc, measurements, theme: getThemeObject(theme), config });
  const measurementRequiredPages = pdfPages.filter((page) => page.layout === PageLayoutModes.grid);

  if (!pdfPages || pdfPages.length < 1) return null;

  //If a license is passed in then we need to wait for the license validation to complete before rendering the component.
  if (license && !licenseValidation) return null;

  const pdfPageLookup = {};
  pdfPages.forEach((page) => pdfPageLookup[page._id] = page);

  const measurementCompleted = Object.keys(measurements).length === measurementRequiredPages.length;

  const pageMeasurementNodes = [];
  pdfPages.forEach((page, index) => {
    if (page.layout === PageLayoutModes.grid) {
      pageMeasurementNodes.push(
        <ExportFormPage
          key={index}
          {...{
            ...page,
            id: page._id,
            licenseValidation,
            config,
            onMeasurementsCompleted: (id, pageMeasurements) => {
              if (!measurements[id]) { setMeasurments((measurements) => ({ ...measurements, [id]: pageMeasurements })); }
            }
          }}
        />
      );
    }
  });


  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <StyledPageWrapper theme={theme}>
        <ThemeProvider value={getThemeObject(theme)}>
          {!measurementCompleted ? (
            <div>
              <div style={{ textAlign: 'center', maxWidth: '400px' }}>
                <h1>Field Measuring In Progress</h1>
                <p>Element with .pdf-capture-ready will be added to the DOM when measuring is completed and PDF is ready to be captured.</p>
              </div>
              <div style={{ opacity: 1 }}>
                {pageMeasurementNodes}
              </div>
            </div>
          ) : (
            <>
              <div id="pdf-capture-ready" className="pdf-capture-ready" />
              <PDFRenderer
                {...{
                  //PDF Export Page Dimensions
                  config,
                  header: pdfHeader,
                  footer: pdfFooter,
                  pages: pdfPages,
                  fieldRenderer: (pageId, field) => (
                    <FieldRenderer
                      field={field}
                      doc={cleanedDoc}
                      licenseValidation={licenseValidation}
                      layout={pdfPageLookup[pageId]?.layout}
                      presentation={pdfPageLookup[pageId]?.presentation}
                      fieldDefaultStyles={cleanedDoc?.files[0]?.styles}
                    />
                  )
                }}
              />
            </>
          )}
        </ThemeProvider>
      </StyledPageWrapper>
    </StyleSheetManager>
  );
};

const FieldRenderer = ({
  field,
  doc,
  licenseValidation,
  fieldDefaultStyles,
  layout = PageLayoutModes.grid,
  presentation = FieldPresentationModes.normal,
}) => {
  return (
    <Field
      {...field}
      docFields={doc?.fields}
      view={FileViews.pdf}
      licenseValidation={licenseValidation}
      disabled
      fieldMode={FieldModes.pdf}
      fieldPresentationMode={presentation}
      pageLayoutMode={layout}
      measure //Used to disable virtualization in table and collection fields
      lazyload={false}
      fieldDefaultStyles={fieldDefaultStyles}
    />
  );
};


const formatPagesForPDFRenderer = createSelector(
  (state) => state.doc,
  (state) => state.measurements,
  (state) => state.theme,
  (state) => state.config,
  (doc, measurements, theme, config) => {

    if (!doc || !doc.fields) return [];

    const fieldLookup = {};
    doc.fields.forEach((field) => {
      if (!fieldLookup[field.file]) fieldLookup[field.file] = {};
      fieldLookup[field.file][field._id] = field;
    });

    let pdfHeader = null;
    let pdfFooter = null;
    const pdfPages = [];

    doc.files.forEach((file) => {


      /**
       * Step 1: Process Header
       */
      if (file.header) {

        let fields = [];

        file.header?.fieldPositions.forEach((fieldPosition) => {

          if (!fieldLookup[file._id][fieldPosition.field]) return;

          const targetField = fieldLookup[file._id][fieldPosition.field] || {};

          fields.push({
            ...targetField,

            /**
             * IMPORTANT: DO NOT REMOVE.
             * - FieldPositon must come after targetField
             * - We format fieldPosition to ensure there are no conflicts with the field
             */
            ...getFormattedFieldPositionForMerging(fieldPosition),

          });

        });

        fields = getPageFieldPositionsForPDFRenerer({
          fields,
          fieldDefaultStyles: file.styles,
          fieldMeasurements: {},
          theme
        });

        pdfHeader = {
          ...file.header,
          fields,
        };

      }

      /**
       * Step 2: Process Footer 
       */
      if (file.footer) {

        let fields = [];

        file.footer?.fieldPositions.forEach((fieldPosition) => {

          if (!fieldLookup[file._id][fieldPosition.field]) return;

          const targetField = fieldLookup[file._id][fieldPosition.field] || {};

          fields.push({
            ...targetField,

            /**
             * IMPORTANT: DO NOT REMOVE.
             * - FieldPositon must come after targetField
             * - We format fieldPosition to ensure there are no conflicts with the field
             */
            ...getFormattedFieldPositionForMerging(fieldPosition),

          });

        });

        fields = getPageFieldPositionsForPDFRenerer({
          fields,
          fieldDefaultStyles: file.styles,
          fieldMeasurements: {},
          theme
        });

        pdfFooter = {
          ...file.footer,
          fields,
        };

      }
      const pages = applyLogic(
        sortPages(file.pages, getPageOrder(file.pageOrder, file.pages)),
        doc.fields,
        '_id'
      ).filter((item) => !item.hidden && !isFieldHiddenInView(item.hiddenViews, FileViews.pdf));

      pages.forEach((page) => {

        let fields = [];

        page.fieldPositions.forEach((fieldPosition) => {

          if (!fieldLookup[file._id][fieldPosition.field]) return;

          const targetField = fieldLookup[file._id][fieldPosition.field] || {};

          fields.push({
            ...targetField,

            /**
             * IMPORTANT: DO NOT REMOVE.
             * - FieldPositon must come after targetField
             * - We format fieldPosition to ensure there are no conflicts with the field
             */
            ...getFormattedFieldPositionForMerging(fieldPosition),

          });

        });

        fields = getPageFieldPositionsForPDFRenerer({
          fields,
          fieldMeasurements: measurements[page._id] || {},
          presentation: page.presentation,
          fieldDefaultStyles: file.styles,
          theme
        });

        fields = applyLogic(fields, doc.fields, '_id').filter((item) => !item.hidden && !isFieldHiddenInView(item.hiddenViews, FileViews.pdf));

        const pageDefaults = { fields, docFields: doc.fields };

        if (page.backgroundImage) {

          /**
           * Previous PDFPages used manual scaling of x, y, width, height, etc. and the PDFRenderer uses CSS scaling on the parent element
           * so everything is automatically scaled. Due to this the field fontSize is scaled differently. We need to adjust the fontSize
           * in order to match the legacy visual requirements.
           */
          const percentageDifference = calculatePagePercentageAdjustments(page.height, page.width, config.page.height, config.page.width);
          pageDefaults.fields = pageDefaults.fields.map((field) => {
            return {
              ...field,
              fontSize: reversePercentageAdjustedValue(field.fontSize || 12, percentageDifference),
            };
          });

          /**
         * We can remove padding because right now legacy simply scales the page
         * and doesn't use the padding value.
         */
          if (page.padding) pageDefaults.padding = 0;

          const defaultBackgroundSize = page.backgroundImage ? '100%' : '';
          pageDefaults.backgroundSize = page.backgroundSize ? file.backgroundSize : defaultBackgroundSize;
          pageDefaults.maxWidth = page.width;
          pageDefaults.maxHeight = page.height;
          pageDefaults.minWidth = page.width;
          pageDefaults.minHeight = page.height;
          pageDefaults.maxHeightType = 'px';
          pageDefaults.maxWidthType = 'px';
          pageDefaults.minWidthType = 'px';
          pageDefaults.minHeightType = 'px';

          /**
           * Previously the PDF Pages were using page.width as the RGL grid cols. The page.cols property
           * is not used for PDF Pages.
           */
          pageDefaults.cols = page.width;
        }

        pdfPages.push({
          ...page,
          ...pageDefaults,
        });

      });

    });

    return { pdfPages, pdfHeader, pdfFooter };

  }
);

/**
 * Handle formatting table field positions for PDF Renderer
 */
export const getTableFormattedPageFieldsForPDFRenerer = createSelector(
  getTableFormattedPageFields,
  (state) => state.fieldMeasurements,
  (fieldPositions, fieldHeightMeasurements) => {

    return fieldPositions.map((fieldPosition) => {

      const nextFieldPosition = { ...fieldPosition };
      const measurements = fieldHeightMeasurements[fieldPosition._id];

      if (fieldPosition.type === FieldTypes.collection || fieldPosition.type === FieldTypes.table) {
        nextFieldPosition.layoutType = PDFRendererFieldLayoutTypes.dynamicVertical;
        nextFieldPosition.layouts = measurements?.layouts;
      }

      return nextFieldPosition;

    });
  }
);

export const getPageFieldPositionsForPDFRenerer = createSelector(
  getTableFormattedPageFieldsForPDFRenerer,
  (state) => state.fieldMeasurements,
  (state) => state.presentation,
  (state) => state.fieldDefaultStyles,
  (state) => state.theme,
  (fieldPositions, fieldHeightMeasurements, fieldPresentationMode, fieldDefaultStyles, theme) => {

    return fieldPositions.map((fieldPosition) => {

      const fieldTheme = getFieldThemeObject({
        mode: FieldModes.readonly,
        fieldPresentationMode,
        fieldPosition,
        fileStyles: fieldDefaultStyles,
        theme: theme?.field,
        sdkTheme: theme
      });

      const nextFieldPosition = { ...fieldPosition, ...fieldTheme };

      if (fieldHeightMeasurements && fieldHeightMeasurements[fieldPosition._id]) {
        nextFieldPosition.height = fieldHeightMeasurements[fieldPosition._id].height;
      }

      return nextFieldPosition;

    });

  }
);

export default React.memo(JoyDocExporter);

